import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, Alert, Snackbar } from "@mui/material";
import { Add as AddIcon, SensorDoor as DoorServiceIcon } from "@mui/icons-material";
import ProjectPicker from "../../pickers/ProjectPicker";
import DoorServiceReportsList from "./DoorServiceReportsList";
import DoorServiceReportFormDialog from "./DoorServiceReportFormDialog";
import DoorServiceReportViewDialog from "./DoorServiceReportViewDialog";
import DeleteReportDialog from "./DeleteReportDialog";
import ReportService from "../../../services/Report.service";

export default function DoorServiceReport() {
    const [project, setProject] = useState(null);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [formDialog, setFormDialog] = useState({ open: false, item: null });
    const [viewDialog, setViewDialog] = useState({ open: false, item: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, report: null });

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const showSnackbar = (message, severity = "success") =>
        setSnackbar({ open: true, message, severity });

    const refresh = useCallback(async () => {
        if (!project) return;
        setLoading(true);
        setError("");
        try {
            const res = await ReportService.listDoorServiceReports({ project: project.id });
            setReports(Array.isArray(res.data) ? res.data : []);
        } catch {
            setError("Failed to load Door Service Reports.");
        } finally {
            setLoading(false);
        }
    }, [project]);

    useEffect(() => {
        if (project) {
            refresh();
        } else {
            setReports([]);
        }
    }, [project, refresh]);

    const handleSaved = () => {
        refresh();
        showSnackbar(formDialog.item ? "Report updated." : "Report created.");
    };

    const handleDeleted = () => {
        refresh();
        showSnackbar("Report deleted.", "info");
    };

    return (
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.5,
                    mb: 2,
                }}
            >
                <Box sx={{ flex: 1 }}>
                    <ProjectPicker value={project} onChange={setProject} size="small" />
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setFormDialog({ open: true, item: null })}
                    sx={{ flexShrink: 0, height: 40 }}
                >
                    New Door Service Report
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!project ? (
                <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    <DoorServiceIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                    <Typography variant="body2">
                        Select a project above to view its Door Service Reports.
                    </Typography>
                </Box>
            ) : (
                <DoorServiceReportsList
                    reports={reports}
                    loading={loading}
                    onView={(report) => setViewDialog({ open: true, item: report })}
                    onEdit={(report) => setFormDialog({ open: true, item: report })}
                    onDelete={(report) => setDeleteDialog({ open: true, report })}
                />
            )}

            <DoorServiceReportFormDialog
                open={formDialog.open}
                onClose={() => setFormDialog({ open: false, item: null })}
                item={formDialog.item}
                initialProject={project}
                onSaved={handleSaved}
            />

            <DoorServiceReportViewDialog
                open={viewDialog.open}
                onClose={() => setViewDialog({ open: false, item: null })}
                report={viewDialog.item}
            />

            <DeleteReportDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, report: null })}
                report={deleteDialog.report}
                onDeleted={handleDeleted}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
